import { AppShellLayout, type AppSidebarSection } from "@civala/shared-ui"
import { useMantineColorScheme } from "@mantine/core"
import {
  IconBook,
  IconBrain,
  IconBriefcase,
  IconRadar,
  IconRobot,
  IconSettings,
  IconShieldCog,
} from "@tabler/icons-react"
import { useLocation, useNavigate } from "@tanstack/react-router"
import { type ElementType, type ReactNode, useEffect, useMemo } from "react"

import { MODES } from "@/data/navigation"
import { useDemoMode } from "@/hooks/useDemoMode"
import { useMe } from "@/hooks/useMe"

import { NexusTopBarLeft, NexusTopBarRight } from "./NexusTopBar"

const ICON_MAP: Record<string, ElementType> = {
  IconBriefcase,
  IconSettings,
  IconRobot,
  IconBrain,
  IconBook,
  IconRadar,
  IconShieldCog,
}

// Full paths of every demo (not-yet-live) panel — used to detect when the current route
// belongs to a screen that demo mode has hidden, so we can redirect off it (see below).
const DEMO_PANEL_PATHS: ReadonlyArray<string> = MODES.flatMap((mode) =>
  mode.panels.filter((panel) => panel.demo).map((panel) => `${mode.path}/${panel.id}`),
)

// Nexus nav is hierarchical (modes → panels). Map it onto the shared AppSidebar shape: a mode
// navigates to its landing panel but stays highlighted for the whole subtree (matchPath =
// mode.path). Two filters compose in one pass: `adminOnly` panels show only to admins, and
// `demo` panels (screens not built on live data yet) are hidden when demo mode is off. A "Demo"
// tag rides every demo panel that IS shown. NB: this is distinct from the page-level `DemoBadge`
// (screen built, data seeded) — a nav-live screen can still show that badge.
const buildSidebarSections = (isAdmin: boolean, demoMode: boolean): ReadonlyArray<AppSidebarSection> =>
  MODES.flatMap((mode) => {
    const visible = mode.panels.filter((panel) => (!panel.adminOnly || isAdmin) && (demoMode || !panel.demo))
    // Drop a mode whose panels are all hidden (e.g. Agents is all-demo when demo mode is off).
    if (visible.length === 0) return []
    // Landing precedence: the pinned defaultPanelId if it survived filtering, else the first
    // visible panel — never raw panels[0], which may now be hidden.
    const landingId = visible.some((p) => p.id === mode.defaultPanelId) ? mode.defaultPanelId! : visible[0].id
    return [
      {
        id: mode.id,
        label: mode.label,
        icon: ICON_MAP[mode.icon] ?? IconBriefcase,
        path: `${mode.path}/${landingId}`,
        matchPath: mode.path,
        children: visible.map((panel) => ({
          id: panel.id,
          label: panel.label,
          path: `${mode.path}/${panel.id}`,
          badge: panel.badge,
          tag: panel.demo ? "Demo" : undefined,
          group: panel.group,
        })),
      },
    ]
  })

type NexusShellProps = {
  readonly children: ReactNode
}

export function NexusShell({ children }: NexusShellProps) {
  const location = useLocation()
  const navigate = useNavigate()

  // Apply the user's saved color scheme on load so it follows them across devices (the
  // server record is the source of truth; the topbar toggle persists changes back).
  const { me } = useMe()
  const { setColorScheme } = useMantineColorScheme()
  useEffect(() => {
    if (me?.preferences.colorScheme) setColorScheme(me.preferences.colorScheme)
  }, [me?.preferences.colorScheme, setColorScheme])

  // Demo mode (device-local, default on): when off, not-yet-live "demo" panels are hidden.
  const [demoMode] = useDemoMode()

  // Role-gated sidebar: admin-only panels (e.g. Users) appear only for admins. The server
  // requireDbRole gate is the real boundary; this just hides the nav affordance.
  const isAdmin = !!me?.roles.includes("admin")
  const sidebarSections = useMemo(() => buildSidebarSections(isAdmin, demoMode), [isAdmin, demoMode])

  // If demo mode is turned off while parked on a now-hidden demo screen, redirect to the first
  // visible landing — otherwise the route matches no visible section and the sidebar looks broken.
  useEffect(() => {
    if (demoMode || sidebarSections.length === 0) return
    const onHidden = DEMO_PANEL_PATHS.some((dp) => location.pathname === dp || location.pathname.startsWith(`${dp}/`))
    if (onHidden) void navigate({ to: sidebarSections[0].path })
  }, [demoMode, location.pathname, sidebarSections, navigate])

  return (
    <AppShellLayout
      persistKey="nexus"
      sidebarSections={sidebarSections}
      sidebarAccordion
      activePath={location.pathname}
      onNavigate={(path) => void navigate({ to: path })}
      brand={{ subtitle: "Nexus" }}
      topBar={{
        left: <NexusTopBarLeft />,
        right: <NexusTopBarRight />,
      }}
      viewTransitionName="nexus-content"
    >
      {children}
    </AppShellLayout>
  )
}
